import {
  resolveBulkDataType,
  type BulkAttributeConfig,
  type BulkAttributeState,
  type BulkAttributeValue,
} from '../../components/bulkUpdate/BulkUpdateInputRenderer';

export type BulkUpdatePreparedUpdates = {
  validatedStates: Record<string, BulkAttributeState>;
  hasError: boolean;
  updates: Array<{ attrInternalName: string; value: BulkAttributeValue }>;
};

type ValidateAndBuildUpdatesParams = {
  attributes: BulkAttributeConfig[];
  sourceStates: Record<string, BulkAttributeState>;
  getInitialValueByType: (attribute: BulkAttributeConfig) => BulkAttributeValue;
};

const NUMBER_PATTERN = /^-?\d*(\.\d*)?$/;

function isSingleSelectValueInvalid(value: BulkAttributeValue): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number') return Number.isNaN(value);
  return true;
}

function getRawNumberValue(value: BulkAttributeValue): string {
  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  return '';
}

function isOutsideNumberRange(attribute: BulkAttributeConfig, parsed: number): boolean {
  if (typeof attribute.attrMinValue === 'number' && parsed < attribute.attrMinValue) {
    return true;
  }

  if (typeof attribute.attrMaxValue === 'number' && parsed > attribute.attrMaxValue) {
    return true;
  }

  return false;
}

function isNumberValueInvalid(attribute: BulkAttributeConfig, value: BulkAttributeValue): boolean {
  const rawValue = getRawNumberValue(value);

  if (!rawValue) return true;

  if (!NUMBER_PATTERN.test(rawValue)) {
    return true;
  }

  const parsed = Number(rawValue);
  if (Number.isNaN(parsed)) {
    return true;
  }

  return isOutsideNumberRange(attribute, parsed);
}

function isTextValueInvalid(value: BulkAttributeValue): boolean {
  const normalizedValue = typeof value === 'string' ? value.trim() : '';
  return !normalizedValue;
}

/**
 * Validates one attribute state according to the attribute data type and min/max constraints.
 */
export function hasValidationError(
  attribute: BulkAttributeConfig,
  state: BulkAttributeState,
): boolean {
  if (!state.enabled) return false;

  const type = resolveBulkDataType(attribute);

  switch (type) {
    case 'date':
      return !(state.value instanceof Date);
    case 'singleSelect':
      return isSingleSelectValueInvalid(state.value);
    case 'number':
      return isNumberValueInvalid(attribute, state.value);
    case 'string':
      return isTextValueInvalid(state.value);
    case 'unknown':
      return isTextValueInvalid(state.value);
  }
}

/**
 * Validates all attribute states and builds the final bulk-update payload for enabled attributes.
 */
export function validateAndBuildUpdates({
  attributes,
  sourceStates,
  getInitialValueByType,
}: ValidateAndBuildUpdatesParams): BulkUpdatePreparedUpdates {
  const validatedStates: Record<string, BulkAttributeState> = {};
  let hasError = false;

  attributes.forEach((attribute) => {
    const previous = sourceStates[attribute.attrInternalName] ?? {
      enabled: false,
      value: getInitialValueByType(attribute),
      error: false,
    };

    const nextError = hasValidationError(attribute, previous);
    if (nextError) hasError = true;

    validatedStates[attribute.attrInternalName] = {
      ...previous,
      error: nextError,
    };
  });

  const updates = attributes
    .map((attribute) => ({
      attrInternalName: attribute.attrInternalName,
      ...validatedStates[attribute.attrInternalName],
    }))
    .filter((item) => item.enabled)
    .map(({ attrInternalName, value }) => ({ attrInternalName, value }));

  return { validatedStates, hasError, updates };
}
